// Maths content data extracted from workbook — master index
// Imports individual section data files and exports the full Grade9Ready maths curriculum map.

import { numbersTopics } from "./mathsSection1";
import { algebraTopics } from "./mathsSection2";
import { graphsTopics } from "./mathsSection3";
import { ratioTopics } from "./mathsSection4";
import { geometryTopics } from "./mathsSection5";
import { probabilityTopics } from "./mathsSection6";
import { examPracticeTopics } from "./mathsSection7";
import { attachExaminerInsights } from "./examinerBank";

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const safeAttachExaminerInsights = (topics = []) => {
  if (!Array.isArray(topics)) return [];

  try {
    return attachExaminerInsights(topics);
  } catch (error) {
    console.warn("[mathsIndex] Failed to attach examiner insights:", error);
    return topics;
  }
};

const countQuestionsInTopic = (topic) => {
  if (!topic?.practice || typeof topic.practice !== "object") return 0;

  return Object.values(topic.practice).reduce((count, level) => {
    return Array.isArray(level) ? count + level.length : count;
  }, 0);
};

const countMarksInTopic = (topic) => {
  if (!topic?.practice || typeof topic.practice !== "object") return 0;

  return Object.values(topic.practice).reduce((total, level) => {
    if (!Array.isArray(level)) return total;

    return (
      total +
      level.reduce((levelTotal, question) => {
        const marks = Number(question?.marks);
        return levelTotal + (Number.isFinite(marks) && marks > 0 ? marks : 1);
      }, 0)
    );
  }, 0);
};

const countQuestionsInSection = (section) => {
  if (!Array.isArray(section?.topics)) return 0;
  return section.topics.reduce((count, topic) => count + countQuestionsInTopic(topic), 0);
};

const countMarksInSection = (section) => {
  if (!Array.isArray(section?.topics)) return 0;
  return section.topics.reduce((count, topic) => count + countMarksInTopic(topic), 0);
};

// -----------------------------------------------------------------------------
// Decorated topic groups
// -----------------------------------------------------------------------------

const _numbers = safeAttachExaminerInsights(numbersTopics);
const _algebra = safeAttachExaminerInsights(algebraTopics);
const _graphs = safeAttachExaminerInsights(graphsTopics);
const _ratio = safeAttachExaminerInsights(ratioTopics);
const _geometry = safeAttachExaminerInsights(geometryTopics);
const _probability = safeAttachExaminerInsights(probabilityTopics);
const _examPractice = safeAttachExaminerInsights(examPracticeTopics);

// -----------------------------------------------------------------------------
// Maths sections
// -----------------------------------------------------------------------------

export const mathsSections = [
  {
    id: 1,
    title: "Numbers",
    slug: "numbers",
    color: "indigo",
    icon: "Calculator",
    description:
      "BIDMAS/BODMAS, ordering, arithmetic, decimals, negatives, primes, factors, multiples, fractions, percentages, rounding, powers, roots and standard form.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/130378ee439d926e9480bd39e24772266595c68ed0b3c038fa4febfd1acb3d30.png",
    topics: _numbers,
  },
  {
    id: 2,
    title: "Algebra",
    slug: "algebra",
    color: "rose",
    icon: "Function",
    description:
      "Simplifying expressions, expanding brackets, factorising, solving equations, formulae, rearranging, sequences and inequalities.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/dc47028fb0ae884556fce4cdfd72f5f143f67153fb848e4f70b0b737f173c1a7.png",
    topics: _algebra,
  },
  {
    id: 3,
    title: "Graphs",
    slug: "graphs",
    color: "emerald",
    icon: "ChartLine",
    description:
      "Coordinates, midpoints, straight-line graphs, gradients, y = mx + c, parallel lines, travel graphs, simultaneous equations and quadratic graphs.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/9f55c801df65070262521ee9dbd6e3e1a64dff1a95549793a81e48ec6772128f.png",
    topics: _graphs,
  },
  {
    id: 4,
    title: "Ratio, Proportion & Rates of Change",
    slug: "ratio-proportion",
    color: "amber",
    icon: "Percent",
    description:
      "Ratios, sharing in a ratio, direct and inverse proportion, percentage change, unit conversions, best buys, density, speed and compound measures.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/11bb660a89e794a8c72241f36b82ec33f42ab58613fcf031cdf77f42f6315d0a.png",
    topics: _ratio,
  },
  {
    id: 5,
    title: "Geometry & Measures",
    slug: "geometry-measures",
    color: "cyan",
    icon: "Cube",
    description:
      "Symmetry, properties of shapes, perimeter, area, circles, 3D shapes, volume, angles, parallel lines, transformations, Pythagoras and trigonometry.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/ca4ece8e0da10b2387c43ba0d53b5de0c4dc2bf18aaeab652c4a005f6071aa56.png",
    topics: _geometry,
  },
  {
    id: 6,
    title: "Probability & Statistics",
    slug: "probability-statistics",
    color: "purple",
    icon: "ChartPie",
    description:
      "Probability basics, relative frequency, Venn diagrams, data types, charts, averages, range, frequency tables and scatter graphs.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/b495b26f8f069b754ab6cd31175e385de4530d59531b5e06f64ab25eb7298e00.png",
    topics: _probability,
  },
  {
    id: 7,
    title: "Exam Practice",
    slug: "exam-practice",
    color: "orange",
    icon: "FileText",
    description:
      "Mixed practice tests, non-calculator paper-style questions and calculator paper-style questions for full GCSE exam preparation.",
    whiteboardImage:
      "https://static.prod-images.emergentagent.com/jobs/0ad73078-9c0c-42e2-abe4-8745d84436dc/images/4197d023f6e3688417340de5db829277f88e3bbebb24a4f05e313cd686f19b29.png",
    topics: _examPractice,
  },
];

// -----------------------------------------------------------------------------
// Enriched section data
// -----------------------------------------------------------------------------

export const mathsSectionsWithStats = mathsSections.map((section) => ({
  ...section,
  topicCount: Array.isArray(section.topics) ? section.topics.length : 0,
  questionCount: countQuestionsInSection(section),
  markCount: countMarksInSection(section),
}));

// -----------------------------------------------------------------------------
// Global helpers
// -----------------------------------------------------------------------------

export const getTotalQuestions = () => {
  return mathsSections.reduce((total, section) => total + countQuestionsInSection(section), 0);
};

export const getTotalMarks = () => {
  return mathsSections.reduce((total, section) => total + countMarksInSection(section), 0);
};

export const getTotalTopics = () => {
  return mathsSections.reduce((total, section) => {
    return total + (Array.isArray(section.topics) ? section.topics.length : 0);
  }, 0);
};

export const getSectionBySlug = (slug) => {
  if (!slug) return null;
  return mathsSections.find((section) => section.slug === slug) || null;
};

export const getSectionById = (id) => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) return null;

  return mathsSections.find((section) => section.id === numericId) || null;
};

export const getTopicBySlug = (topicSlug) => {
  if (!topicSlug) return null;

  for (const section of mathsSections) {
    const topic = section.topics?.find((item) => item.slug === topicSlug);

    if (topic) {
      return {
        ...topic,
        sectionId: section.id,
        sectionTitle: section.title,
        sectionSlug: section.slug,
        sectionColor: section.color,
      };
    }
  }

  return null;
};

export const getTopicById = (topicId) => {
  if (!topicId) return null;

  for (const section of mathsSections) {
    const topic = section.topics?.find((item) => String(item.id) === String(topicId));

    if (topic) {
      return {
        ...topic,
        sectionId: section.id,
        sectionTitle: section.title,
        sectionSlug: section.slug,
        sectionColor: section.color,
      };
    }
  }

  return null;
};

export const getAllTopics = () => {
  return mathsSections.flatMap((section) =>
    (section.topics || []).map((topic) => ({
      ...topic,
      sectionId: section.id,
      sectionTitle: section.title,
      sectionSlug: section.slug,
      sectionColor: section.color,
    }))
  );
};

export const getAllQuestions = () => {
  return getAllTopics().flatMap((topic) => {
    if (!topic.practice || typeof topic.practice !== "object") return [];

    return Object.entries(topic.practice).flatMap(([levelKey, questions]) => {
      if (!Array.isArray(questions)) return [];

      return questions.map((question) => ({
        ...question,
        levelKey,
        topicId: topic.id,
        topicTitle: topic.title,
        topicSlug: topic.slug,
        sectionId: topic.sectionId,
        sectionTitle: topic.sectionTitle,
        sectionSlug: topic.sectionSlug,
        sectionColor: topic.sectionColor,
      }));
    });
  });
};

export const getQuestionsByDifficulty = (difficulty) => {
  if (!difficulty) return [];

  return getAllQuestions().filter((question) => {
    const topic = getTopicById(question.topicId);
    return topic?.difficulty === difficulty;
  });
};

export const getMathsSummary = () => {
  return {
    sectionCount: mathsSections.length,
    topicCount: getTotalTopics(),
    questionCount: getTotalQuestions(),
    markCount: getTotalMarks(),
    sections: mathsSectionsWithStats.map((section) => ({
      id: section.id,
      title: section.title,
      slug: section.slug,
      topicCount: section.topicCount,
      questionCount: section.questionCount,
      markCount: section.markCount,
    })),
  };
};

export default mathsSections;